<template>
  <div class="mx-auto p-4 bg-warm-gray-900 min-h-screen pb-20">
    <div class="w-full mx-auto">
      <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 class="text-2xl font-bold text-gray-100">CAAP Reviewer Data</h1>
        <router-link
          to="/scorecard/recent"
          class="px-3 py-2 text-sm bg-warm-gray-800 hover:bg-warm-gray-700 border border-warm-gray-700 rounded text-gray-200 transition-colors"
        >
          Recent Scorecards
        </router-link>
      </div>
      
      <!-- Month Filter -->
      <div class="mb-6">
        <div class="flex items-center gap-4">
          <button
            @click="previousMonth"
            :disabled="loadingExamResults"
            class="px-3 py-2 text-gray-300 bg-warm-gray-800 hover:bg-warm-gray-700 border border-warm-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-warm-gray-800"
          >
            &larr;
          </button>
          <div class="text-lg font-semibold text-gray-100 min-w-[200px] text-center">
            {{ currentMonthDisplay }}
          </div>
          <button
            @click="nextMonth"
            :disabled="loadingExamResults"
            class="px-3 py-2 text-gray-300 bg-warm-gray-800 hover:bg-warm-gray-700 border border-warm-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-warm-gray-800"
          >
            &rarr;
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loadingExamResults" class="flex justify-center items-center py-12">
        <svg
          class="animate-spin h-8 w-8 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>

      <!-- Subjects with Exam Results Tables -->
      <div v-else class="space-y-8">
        <div v-if="examResultsBySubjectGrouped.length === 0" class="text-center py-12">
          <p class="text-gray-400 text-lg">No exam results found for {{ currentMonthDisplay }}</p>
        </div>
        
        <div
          v-for="subjectGroup in examResultsBySubjectGrouped"
          :key="subjectGroup.subjectId"
          class="bg-warm-gray-800 rounded-lg p-6 border border-warm-gray-700"
        >
          <!-- Header -->
          <div class="flex items-center justify-between mb-4">
            <div>
              <h2 class="text-xl font-semibold text-gray-100 mb-1">
                {{ getSubjectName(subjectGroup.subjectId) }}
              </h2>
              <p class="text-sm text-gray-400">
                ID: {{ subjectGroup.subjectId }} • 
                {{ subjectGroup.results.length }} submission{{ subjectGroup.results.length !== 1 ? 's' : '' }}
              </p>
            </div>
            <div class="flex items-center gap-2">
              <button
                @click="filterResultsByCaapDates(subjectGroup.subjectId)"
                :disabled="filteringSubjects.has(subjectGroup.subjectId) || loadingExamResults"
                class="px-3 py-1 text-xs bg-blue-700 hover:bg-blue-600 text-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-700 flex items-center gap-1"
              >
                <svg
                  v-if="filteringSubjects.has(subjectGroup.subjectId)"
                  class="animate-spin h-3 w-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Filter by CAAP Dates
              </button>
              <button
                @click="toggleSubjectTable(subjectGroup.subjectId)"
                class="px-3 py-1 text-xs bg-warm-gray-700 hover:bg-warm-gray-600 text-gray-300 rounded transition-colors"
              >
                {{ expandedSubjectTables.has(subjectGroup.subjectId) ? '▼ Hide ' : '▶ Show ' }} {{ subjectGroup.results.length }} Submissions
              </button>
            </div>
          </div>

          <!-- Metrics Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <!-- Average Score -->
            <div class="bg-warm-gray-700/50 rounded-lg p-3 border border-warm-gray-600">
              <div class="flex items-center justify-between mb-2">
                <p class="text-xs text-gray-400">Average Score</p>
                <span
                  v-if="subjectGroup.variance !== null && subjectGroup.previousMonthAverage !== null && subjectGroup.previousMonthAverage !== undefined"
                  :class="[
                    'text-xs font-medium px-1.5 py-0.5 rounded',
                    subjectGroup.variance > 0
                      ? 'bg-green-900 text-green-300'
                      : subjectGroup.variance < 0
                        ? 'bg-red-900 text-red-300'
                        : 'bg-warm-gray-700 text-gray-400'
                  ]"
                  :title="`${subjectGroup.variance > 0 ? '+' : ''}${subjectGroup.variance.toFixed(1)}% change`"
                >
                  {{ subjectGroup.variance > 0 ? '↑' : subjectGroup.variance < 0 ? '↓' : '→' }}
                  {{ Math.abs(subjectGroup.variance).toFixed(1) }}%
                </span>
              </div>
              <div class="flex items-baseline flex-col">
                <p class="text-2xl font-bold" :class="getAverageScoreColor(subjectGroup.averageScore)">
                  {{ subjectGroup.averageScore.toFixed(1) }}%
                </p>
                <p class="text-xs text-gray-500">{{ currentMonthDisplay }}</p>
              </div>
              <div v-if="subjectGroup.previousMonthAverage !== null && subjectGroup.previousMonthAverage !== undefined" class="pt-1 border-t border-warm-gray-600 mt-1">
                <p class="text-sm font-semibold text-gray-400">
                  {{ subjectGroup.previousMonthAverage.toFixed(1) }}%
                </p>
                <p class="text-xs text-gray-500">{{ getPreviousMonthDisplay() }}</p>
              </div>
            </div>

            <!-- App/Exam Items -->
            <div class="bg-warm-gray-700/50 rounded-lg p-3 border border-warm-gray-600">
              <div class="flex items-center justify-between mb-2">
                <p class="text-xs text-gray-400">App / Exam Items</p>
                <div v-if="subjectGroup.varianceItemsApp !== null">
                  <span
                    :class="[
                      'text-xs font-medium px-1.5 py-0.5 rounded',
                      subjectGroup.varianceItemsApp > 0
                        ? 'bg-green-900 text-green-300'
                        : subjectGroup.varianceItemsApp < 0
                          ? 'bg-red-900 text-red-300'
                          : 'bg-warm-gray-700 text-gray-400'
                    ]"
                    :title="`App: ${subjectGroup.varianceItemsApp > 0 ? '+' : ''}${subjectGroup.varianceItemsApp.toFixed(2)} change`"
                  >
                    {{ subjectGroup.varianceItemsApp > 0 ? '↑' : subjectGroup.varianceItemsApp < 0 ? '↓' : '→' }}
                    {{ Math.abs(subjectGroup.varianceItemsApp).toFixed(2) }}
                  </span>
                </div>
              </div>
              <div v-if="subjectGroup.averageItemsApp !== null && subjectGroup.averageItemsExam !== null" class="space-y-1">
                <div>
                  <p class="text-2xl font-semibold text-gray-200">
                    {{ subjectGroup.averageItemsApp.toFixed(2) }} / {{ subjectGroup.averageItemsExam.toFixed(2) }}
                  </p>
                  <p class="text-xs text-gray-500">{{ currentMonthDisplay }}</p>
                </div>
                <div v-if="subjectGroup.previousMonthItemsApp !== null && subjectGroup.previousMonthItemsExam !== null" class="pt-1 border-t border-warm-gray-600">
                  <p class="text-sm font-semibold text-gray-400">
                    {{ subjectGroup.previousMonthItemsApp.toFixed(2) }} / {{ subjectGroup.previousMonthItemsExam.toFixed(2) }}
                  </p>
                  <p class="text-xs text-gray-500">{{ getPreviousMonthDisplay() }}</p>
                </div>
              </div>
              <p v-else class="text-xs text-gray-500 italic">No data</p>
            </div>

            <!-- Passing Rate -->
            <div class="bg-warm-gray-700/50 rounded-lg p-3 border border-warm-gray-600">
              <div class="flex items-center justify-between mb-2">
                <p class="text-xs text-gray-400">Passing Rate</p>
                <span
                  v-if="subjectGroup.variancePassingRate !== null && subjectGroup.previousMonthPassingRate !== null && subjectGroup.previousMonthPassingRate !== undefined"
                  :class="[
                    'text-xs font-medium px-1.5 py-0.5 rounded',
                    subjectGroup.variancePassingRate > 0
                      ? 'bg-green-900 text-green-300'
                      : subjectGroup.variancePassingRate < 0
                        ? 'bg-red-900 text-red-300'
                        : 'bg-warm-gray-700 text-gray-400'
                  ]"
                  :title="`${subjectGroup.variancePassingRate > 0 ? '+' : ''}${subjectGroup.variancePassingRate.toFixed(1)}% change`"
                >
                  {{ subjectGroup.variancePassingRate > 0 ? '↑' : subjectGroup.variancePassingRate < 0 ? '↓' : '→' }}
                  {{ Math.abs(subjectGroup.variancePassingRate).toFixed(1) }}%
                </span>
              </div>
              <div v-if="subjectGroup.passingRate !== null">
                <div class="flex items-baseline gap-2 mb-1">
                  <span
                    :class="[
                      'text-xl font-bold',
                      subjectGroup.passingRate < 80
                        ? 'text-red-400'
                        : 'text-green-400'
                    ]"
                  >
                    {{ subjectGroup.passingRate.toFixed(1) }}%
                  </span>
                  <span
                    v-if="subjectGroup.passingRate < 80"
                    class="text-xs font-medium px-1.5 py-0.5 rounded bg-red-900/30 text-red-300 border border-red-700"
                    title="Passing rate is below 80% threshold"
                  >
                    ⚠
                  </span>
                </div>
                <p class="text-xs text-gray-500 mb-1">
                  <span>{{ currentMonthDisplay }}</span>
                  <span class="text-xs text-gray-500"> [{{ subjectGroup.passedCount }} / {{ subjectGroup.totalExamTakers }} passed]</span>
                </p>
                <div v-if="subjectGroup.previousMonthPassingRate !== null && subjectGroup.previousMonthPassingRate !== undefined" class="mt-1 pt-1 border-t border-warm-gray-600">
                  <p class="text-sm font-semibold text-gray-400">
                    <span>{{ subjectGroup.previousMonthPassingRate.toFixed(1) }}%</span>
                  </p>
                  <p class="text-xs text-gray-500">
                    <span>{{ getPreviousMonthDisplay() }}</span>
                    <span class="text-xs text-gray-500"> [{{ subjectGroup.previousMonthPassedCount }} / {{ subjectGroup.previousMonthTotalExamTakers }} passed]</span>
                  </p>
                </div>
              </div>
              <p v-else class="text-xs text-gray-500 italic">No data</p>
            </div>

            <!-- Failing Rate -->
            <div class="bg-warm-gray-700/50 rounded-lg p-3 border border-warm-gray-600">
              <p class="text-xs text-gray-400 mb-2">Failing Rate</p>
              <div v-if="subjectGroup.failingRate !== null">
                <div class="flex items-baseline gap-2 mb-1">
                  <span
                    :class="[
                      'text-xl font-bold',
                      subjectGroup.failingRate >= 20
                        ? 'text-red-400'
                        : subjectGroup.failingRate > 5
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                    ]"
                  >
                    {{ subjectGroup.failingRate.toFixed(1) }}%
                  </span>
                  <span
                    v-if="subjectGroup.failingRate >= 20"
                    class="text-xs font-medium px-1.5 py-0.5 rounded bg-red-900/30 text-red-300 border border-red-700"
                    title="Failing rate is 20% or above - critical alert"
                  >
                    ⚠
                  </span>
                  <span
                    v-else-if="subjectGroup.failingRate > 5"
                    class="text-xs font-medium px-1.5 py-0.5 rounded bg-yellow-900/30 text-yellow-300 border border-yellow-700"
                    title="Failing rate is above 5% threshold"
                  >
                    ⚠
                  </span>
                </div>
                <p class="text-xs text-gray-500 mb-1">
                  <span>{{ currentMonthDisplay }}</span>
                  <span class="text-xs text-gray-500"> [{{ subjectGroup.failedCount }} / {{ subjectGroup.totalExamTakers }} failed]</span>
                </p>
                <div v-if="subjectGroup.previousMonthFailingRate !== null && subjectGroup.previousMonthFailingRate !== undefined" class="mt-1 pt-1 border-t border-warm-gray-600">
                  <p class="text-sm font-semibold text-gray-400">
                    {{ subjectGroup.previousMonthFailingRate.toFixed(1) }}%
                  </p>
                  <p class="text-xs text-gray-500">
                    <span>{{ getPreviousMonthDisplay() }}</span>
                    <span class="text-xs text-gray-500"> [{{ subjectGroup.previousMonthFailedCount }} / {{ subjectGroup.previousMonthTotalExamTakers }} failed]</span>
                  </p>
                </div>
              </div>
              <p v-else class="text-xs text-gray-500 italic">No data</p>
            </div>
          </div>

          <!-- Subtopics List -->
          <div class="flex flex-wrap items-center gap-2">
            <div
              v-for="subtopicInfo in getSubjectSubtopicsWithVotes(subjectGroup.subjectId, subjectGroup.results)"
              :key="subtopicInfo.id"
              class="px-2 py-1 border border-warm-gray-600 rounded text-xs text-gray-400"
            >
              <span class="font-medium">{{ subtopicInfo.name }}</span>
              <span class="bg-warm-gray-600 px-2 text-white ml-1 rounded-md text-xs">{{ subtopicInfo.voteCount }}</span>
            </div>
            <span v-if="getSubjectSubtopicsWithVotes(subjectGroup.subjectId, subjectGroup.results).length === 0" class="text-xs text-gray-500 italic">
              No subtopics
            </span>
          </div>
          
          <!-- Exam Results Table -->
          <div v-if="expandedSubjectTables.has(subjectGroup.subjectId)" class="overflow-x-auto mt-4">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-warm-gray-700 border-b border-warm-gray-600">
                  <th class="px-2 py-3 text-center w-10">
                    <input
                      type="checkbox"
                      class="h-4 w-4 rounded border-warm-gray-500 bg-warm-gray-800 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-warm-gray-900 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      :checked="areAllResultsMarked(subjectGroup.results)"
                      :disabled="markingResultIds.size > 0 || subjectGroup.results.length === 0"
                      @change="toggleAllMarkersForSubject(subjectGroup)"
                      @click.stop
                    />
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Email</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Dates</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Subtopics</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Comments</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Score</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Items App / Exam</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Status</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Actions</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase w-16">Hide</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-warm-gray-700">
                <tr
                  v-for="result in subjectGroup.results"
                  :key="result.id"
                  @click.stop
                  :class="[
                    'transition-colors',
                    hiddenResultIds.has(result.id) 
                      ? 'opacity-50 bg-warm-gray-700/30' 
                      : 'hover:bg-warm-gray-700/50'
                  ]"
                >
                  <td class="px-2 py-3 text-center">
                    <input
                      type="checkbox"
                      class="h-4 w-4 rounded border-warm-gray-500 bg-warm-gray-800 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-warm-gray-900 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      :checked="markedResultIds.has(result.id)"
                      :disabled="markingResultIds.has(result.id) || markingResultIds.size > 0"
                      @change="toggleResultMarker(result)"
                      @click.stop
                    />
                  </td>
                  <td :class="['px-4 py-3', hiddenResultIds.has(result.id) ? 'text-gray-500' : 'text-gray-200']">
                    <button
                      @click.stop="openUserSubmissionsModal(result.data.userId, result.data.email)"
                      class="text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                    >
                      {{ result.data.email || 'N/A' }}
                    </button>
                  </td>
                  <td :class="['px-4 py-3 text-xs', hiddenResultIds.has(result.id) ? 'text-gray-600' : 'text-gray-400']">
                    <div>Exam: {{ formatExamDate(result.data.examDate) }}</div>
                    <div>Submitted: {{ formatExamDate(result.data.submittedDate) }}</div>
                    <div v-if="resultCaapDates.has(result.id)" class="mt-2 pt-2 border-t border-warm-gray-600">
                      <div v-if="resultCaapDates.get(result.id).activationDate && resultCaapDates.get(result.id).expirationDate">
                        <div :class="resultCaapDates.get(result.id).isWithinCurrentRange ? 'text-green-400' : 'text-red-400'">
                          Activation: {{ formatCaapDate(resultCaapDates.get(result.id).activationDate) }}
                        </div>
                        <div :class="resultCaapDates.get(result.id).isWithinCurrentRange ? 'text-green-400' : 'text-red-400'">
                          Expiration: {{ formatCaapDate(resultCaapDates.get(result.id).expirationDate) }}
                        </div>
                        <div
                          v-if="resultCaapDates.get(result.id).isValidUnderPriorSubscription"
                          class="mt-1 text-amber-300"
                        >
                          Valid under prior subscription
                        </div>
                      </div>
                      <div v-else-if="resultCaapDates.get(result.id).isValidUnderPriorSubscription" class="text-amber-300">
                        Valid under prior subscription
                      </div>
                      <div v-else class="text-yellow-400">
                        Not found
                      </div>
                    </div>
                  </td>
                  <td :class="['px-4 py-3 text-xs', hiddenResultIds.has(result.id) ? 'text-gray-600' : 'text-gray-400']">
                    <div v-if="result.data.subtopicVotes && result.data.subtopicVotes.length > 0" class="space-y-1">
                      <div v-for="subtopicId in result.data.subtopicVotes" :key="subtopicId" class="flex items-start">
                        <span class="mr-2">•</span>
                        <span>{{ getSubtopicName(subtopicId) || subtopicId }}</span>
                      </div>
                    </div>
                    <span v-else class="text-gray-500 italic">No subtopics</span>
                  </td>
                  <td :class="['px-4 py-3 text-xs max-w-xs whitespace-pre-wrap break-words', hiddenResultIds.has(result.id) ? 'text-gray-600' : 'text-gray-400']">
                    <span v-if="hasComment(result.data.comments)">{{ result.data.comments }}</span>
                    <span v-else class="text-gray-500 italic">—</span>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <span
                      :class="[
                        'font-semibold',
                        hiddenResultIds.has(result.id)
                          ? 'text-gray-500'
                          : result.data.isPassed 
                            ? 'text-green-400' 
                            : 'text-red-400'
                      ]"
                    >
                      {{ result.data.score || 0 }}%
                    </span>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <span :class="hiddenResultIds.has(result.id) ? 'text-gray-500' : 'text-gray-300'">
                      <span v-if="result.data.numItemsFromApp != null && result.data.numItemsInExam != null">
                        {{ result.data.numItemsFromApp }} / {{ result.data.numItemsInExam }}
                      </span>
                      <span v-else>N/A</span>
                    </span>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <span
                      :class="[
                        'px-2 py-1 rounded text-xs font-medium whitespace-nowrap',
                        hiddenResultIds.has(result.id)
                          ? 'bg-gray-700 text-gray-500'
                          : result.data.isPassed 
                            ? 'bg-green-900 text-green-200' 
                            : 'bg-red-900 text-red-200'
                      ]"
                    >
                      {{ result.data.isPassed ? '✓ Passed' : '✗ Failed' }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <button
                      @click.stop="openDetailsModal(result)"
                      :class="[
                        'px-3 py-1 text-xs rounded transition-colors',
                        hiddenResultIds.has(result.id)
                          ? 'bg-warm-gray-800 text-gray-600 cursor-not-allowed'
                          : 'bg-warm-gray-700 hover:bg-warm-gray-600 text-gray-300'
                      ]"
                      :disabled="hiddenResultIds.has(result.id)"
                    >
                      View Raw
                    </button>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <button
                      @click.stop="toggleHideResult(result.id)"
                      class="p-1 text-gray-400 hover:text-gray-200 transition-colors"
                      :title="hiddenResultIds.has(result.id) ? 'Show this submission' : 'Hide this submission'"
                    >
                      <svg
                        v-if="hiddenResultIds.has(result.id)"
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l13.42 13.42"
                        />
                      </svg>
                      <svg
                        v-else
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      
        <!-- Submitted Questions Table -->
      <div v-if="submittedQuestions.length > 0" class="mt-12 mb-20">
        <h2 class="text-xl font-bold mb-4 text-gray-100">Submitted Questions</h2>
        <div class="bg-warm-gray-800 rounded-lg border border-warm-gray-700 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-warm-gray-700 border-b border-warm-gray-600">
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">User</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Question</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Subject</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-warm-gray-700">
                <tr
                  v-for="(item, index) in submittedQuestions"
                  :key="index"
                  class="hover:bg-warm-gray-700/50 transition-colors"
                >
                  <td class="px-4 py-3">
                    <button
                      @click="openUserSubmissionsModal(item.userId, item.email)"
                      class="text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                    >
                      {{ item.email || 'N/A' }}
                    </button>
                  </td>
                  <td class="px-4 py-3">
                    <div class="text-gray-200">
                      <p class="mb-1">{{ item.question }}</p>
                      <div v-if="item.choices && item.choices.length > 0" class="mt-1">
                        <p class="text-xs text-gray-400">Choices:</p>
                        <ul class="list-disc list-inside text-xs text-gray-400 ml-2">
                          <li v-for="(choice, choiceIndex) in item.choices" :key="choiceIndex">
                            {{ choice }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-gray-300">
                    {{ getSubjectName(item.subjectId) || item.subjectId }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          </div>
      </div>
      
      </div>
    </div>
  </div>

  <!-- Details Modal -->
  <div
    v-if="selectedResult"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click.self="closeDetailsModal"
  >
    <div class="bg-warm-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-xl font-bold text-gray-100">Exam Result Details</h2>
        <button
          @click="closeDetailsModal"
          class="text-gray-400 hover:text-gray-200 transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div>
        <h3 class="text-sm font-semibold text-gray-300 mb-2">Raw Data</h3>
        <pre class="text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap bg-warm-gray-900 p-4 rounded border border-warm-gray-700">{{ JSON.stringify(selectedResult.data, null, 2) }}</pre>
      </div>
    </div>
  </div>

  <!-- User Submissions Modal -->
  <div
    v-if="selectedUserEmail"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-4"
    @click.self="closeUserSubmissionsModal"
  >
    <div class="bg-warm-gray-800 rounded-lg p-6 max-w-6xl w-full mx-4 my-4">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-gray-100">All Submissions for {{ selectedUserEmail }}</h2>
            <button
              @click="closeUserSubmissionsModal"
              class="text-gray-400 hover:text-gray-200 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- KTRs Section -->
          <div class="mb-6 p-4 bg-warm-gray-900 rounded border border-warm-gray-700">
            <h3 class="text-sm font-semibold text-gray-300 mb-3">Knowledge Test Results (KTRs)</h3>
            
            <!-- Loading State -->
            <div v-if="loadingKtrs" class="flex items-center gap-2 text-sm text-gray-400">
              <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading KTRs...</span>
            </div>
            
            <!-- KTRs List as Links -->
            <div v-else-if="selectedResultKtrs.length > 0" class="space-y-2">
              <a
                v-for="(ktr, index) in selectedResultKtrs"
                :key="index"
                :href="ktr.url"
                target="_blank"
                rel="noopener noreferrer"
                class="block px-3 py-2 bg-warm-gray-800 hover:bg-warm-gray-700 border border-warm-gray-600 rounded text-sm transition-colors break-all"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <span class="mr-2">🔗</span>
                    <span class="text-blue-400 hover:text-blue-300">
                      <span v-if="ktr.filenameBeforeUpload">{{ ktr.filenameBeforeUpload }}</span>
                      <span v-else>{{ ktr.url }}</span>
                    </span>
                  </div>
                  <span v-if="ktr.uploadedAt" class="text-xs text-gray-500 ml-3 flex-shrink-0">
                    {{ formatKtrDate(ktr.uploadedAt) }}
                  </span>
                </div>
              </a>
            </div>
            
            <!-- No KTRs State -->
            <p v-else class="text-sm text-gray-500 italic">No Knowledge Test Results available</p>
          </div>

          <!-- Loading State -->
          <div v-if="loadingUserSubmissions" class="flex justify-center items-center py-12">
            <svg
              class="animate-spin h-8 w-8 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>

          <!-- All User Submissions by Subject -->
          <div v-else class="space-y-6">
            <div
              v-for="subject in sortedSubjects"
              :key="subject.id"
              class="bg-warm-gray-900 rounded-lg p-4 border border-warm-gray-700"
            >
              <h3 class="text-lg font-semibold text-gray-100 mb-3">
                {{ subject.data.name }}
              </h3>
              
              <!-- Get submissions for this subject -->
              <div v-if="getSubmissionsForSubject(subject.id).length > 0">
                <p class="text-sm text-gray-400 mb-4">
                  {{ getSubmissionsForSubject(subject.id).length }} submission{{ getSubmissionsForSubject(subject.id).length !== 1 ? 's' : '' }}
                </p>

                <!-- Submissions Table -->
                <div class="overflow-x-auto">
                  <table class="w-full text-sm border-collapse">
                    <thead>
                      <tr class="bg-warm-gray-800 border-b border-warm-gray-600">
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Dates</th>
                        <th class="px-4 py-2 text-center text-xs font-medium text-gray-300 uppercase">Score</th>
                        <th class="px-4 py-2 text-center text-xs font-medium text-gray-300 uppercase">Status</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Comments</th>
                        <th class="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Subtopics</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-warm-gray-700">
                      <tr
                        v-for="submission in getSubmissionsForSubject(subject.id)"
                        :key="submission.id"
                        class="hover:bg-warm-gray-800/50 transition-colors"
                      >
                        <td class="px-4 py-2 text-gray-400 text-xs">
                          <div class="flex flex-col">
                            <span><strong>Exam:</strong> {{ formatExamDate(submission.data.examDate) }}</span>
                            <span><strong>Submitted:</strong> {{ formatExamDate(submission.data.submittedDate) }}</span>
                          </div>
                        </td>
                        <td class="px-4 py-2 text-center">
                          <span
                            :class="[
                              'font-semibold',
                              submission.data.isPassed ? 'text-green-400' : 'text-red-400'
                            ]"
                          >
                            {{ submission.data.score || 0 }}%
                          </span>
                        </td>
                        <td class="px-4 py-2 text-center">
                          <span
                            :class="[
                              'px-2 py-1 rounded text-xs font-medium whitespace-nowrap',
                              submission.data.isPassed 
                                ? 'bg-green-900 text-green-200' 
                                : 'bg-red-900 text-red-200'
                            ]"
                          >
                            {{ submission.data.isPassed ? '✓ Passed' : '✗ Failed' }}
                          </span>
                        </td>
                        <td class="px-4 py-2 text-left">
                          <span v-if="hasComment(submission.data.comments)" class="text-xs text-gray-400 whitespace-pre-wrap break-words">
                            {{ submission.data.comments }}
                          </span>
                          <span v-else class="text-xs text-gray-500 italic">—</span>
                        </td>
                        <td class="px-4 py-2 text-xs text-gray-400">
                          <div v-if="submission.data.subtopicVotes && submission.data.subtopicVotes.length > 0" class="space-y-1">
                            <div v-for="subtopicId in submission.data.subtopicVotes" :key="subtopicId" class="flex items-start">
                              <span class="mr-2">•</span>
                              <span>{{ getSubtopicName(subtopicId) || subtopicId }}</span>
                            </div>
                          </div>
                          <span v-else class="text-gray-500 italic">No subtopics</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              <!-- No submissions for this subject -->
              <div v-else class="text-sm text-gray-500 italic py-2">
                No submissions for this subject
              </div>
            </div>
          </div>
        </div>
      </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { getCaapSubjects, getCaapSubtopics, getExamResultsByMonth, getUserKtrs, getAllExamResultsByUserId, getUserCaapData, getUserConfirmedCaapKnowledgePeriods, getScorecardMarkersByMonth, markScorecardResult, unmarkScorecardResult, setScorecardMarkersForResults, formatScorecardYearMonth } from './firebase'

const subjects = ref([])
const subtopics = ref([])
const loading = ref(false)

// Month filter state
const currentMonth = ref(new Date().getMonth())
const currentYear = ref(new Date().getFullYear())

// Exam results state
const examResults = ref([])
const previousMonthExamResults = ref([])
const loadingExamResults = ref(false)

// Modal state
const selectedResult = ref(null)

// KTRs state for the modal
const selectedResultKtrs = ref([])
const loadingKtrs = ref(false)

// User submissions modal state
const selectedUserEmail = ref(null)
const selectedUserId = ref(null)
const userSubmissions = ref([])
const loadingUserSubmissions = ref(false)
const userSubmissionsBySubject = ref(new Map())

// Track which subjects have their exam results table expanded
const expandedSubjectTables = ref(new Set())

// Track which exam result IDs are hidden (not counted in averages)
const hiddenResultIds = ref(new Set())

// Shared scorecard markers (persisted in Firestore; visible to all admins)
const markedResultIds = ref(new Set())
const markingResultIds = ref(new Set())

// CAAP date filtering state
const filteringSubjects = ref(new Set())
// Map<resultId, { activationDate, expirationDate, isWithinCurrentRange, isValidUnderPriorSubscription, isWithinRange }>
const resultCaapDates = ref(new Map())

/**
 * Get month display string (e.g., "November 2025")
 */
const currentMonthDisplay = computed(() => {
  const date = new Date(currentYear.value, currentMonth.value, 1)
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
})

/**
 * Get previous month and year
 */
const getPreviousMonth = (month, year) => {
  if (month === 0) {
    return { month: 11, year: year - 1 }
  }
  return { month: month - 1, year }
}

/**
 * Get previous month display string
 */
const getPreviousMonthDisplay = () => {
  const { month: prevMonth, year: prevYear } = getPreviousMonth(currentMonth.value, currentYear.value)
  const date = new Date(prevYear, prevMonth, 1)
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

/**
 * Get subjects sorted alphabetically by name
 */
const sortedSubjects = computed(() => {
  return [...subjects.value].sort((a, b) => {
    const nameA = a.data.name || ''
    const nameB = b.data.name || ''
    return nameA.localeCompare(nameB)
  })
})

/**
 * Get subject name by ID
 */
const getSubjectName = (subjectId) => {
  const subject = subjects.value.find(s => s.id === subjectId)
  return subject ? subject.data.name : subjectId || 'Unknown Subject'
}

/**
 * Get subtopic name by ID
 */
const getSubtopicName = (subtopicId) => {
  const subtopic = subtopics.value.find(s => s.id === subtopicId)
  return subtopic ? subtopic.data.name : null
}

/**
 * Whether a submission has a non-empty comment
 */
const hasComment = (comments) => {
  return typeof comments === 'string' && comments.trim().length > 0
}

/**
 * Get subtopics for a subject with vote counts from exam results
 */
const getSubjectSubtopicsWithVotes = (subjectId, results) => {
  // Find the subject
  const subject = subjects.value.find(s => s.id === subjectId)
  if (!subject || !subject.data.exam || !subject.data.exam.subtopics) {
    return []
  }
  
  // Get subtopic IDs for this subject
  const subjectSubtopicIds = subject.data.exam.subtopics
  
  // Count votes for each subtopic from the results
  const voteCounts = new Map()
  results.forEach(result => {
    const subtopicVotes = result.data.subtopicVotes || []
    subtopicVotes.forEach(subtopicId => {
      if (subjectSubtopicIds.includes(subtopicId)) {
        const currentCount = voteCounts.get(subtopicId) || 0
        voteCounts.set(subtopicId, currentCount + 1)
      }
    })
  })
  
  // Build array of subtopics with names and vote counts
  const subtopicsWithVotes = subjectSubtopicIds.map(subtopicId => {
    const subtopic = subtopics.value.find(s => s.id === subtopicId)
    return {
      id: subtopicId,
      name: subtopic ? subtopic.data.name : subtopicId,
      voteCount: voteCounts.get(subtopicId) || 0
    }
  })
  
  // Sort by vote count (descending - most votes first)
  return subtopicsWithVotes.sort((a, b) => b.voteCount - a.voteCount)
}

/**
 * Format examDate timestamp to readable string
 */
const formatExamDate = (examDate) => {
  if (!examDate || !examDate.seconds) return 'N/A'
  
  const date = new Date(examDate.seconds * 1000)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

/**
 * Format KTR uploadedAt timestamp to readable string
 */
const formatKtrDate = (uploadedAt) => {
  if (!uploadedAt || !uploadedAt.seconds) return 'N/A'
  
  const date = new Date(uploadedAt.seconds * 1000)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Get color class for average score based on passing threshold (assuming 70% is passing)
 */
const getAverageScoreColor = (averageScore) => {
  // Assuming 70% is the passing threshold
  return averageScore >= 70 ? 'text-green-400' : 'text-red-400'
}

/**
 * Open details modal with selected result and fetch KTRs
 */
const openDetailsModal = async (result) => {
  selectedResult.value = result
  selectedResultKtrs.value = []
  loadingKtrs.value = true
  
  // Fetch KTRs only when modal opens
  const userId = result.data.userId
  if (userId) {
    try {
      selectedResultKtrs.value = await getUserKtrs(userId)
    } catch (error) {
      console.error('Error fetching KTRs:', error)
      selectedResultKtrs.value = []
    }
  }
  
  loadingKtrs.value = false
}

/**
 * Close details modal
 */
const closeDetailsModal = () => {
  selectedResult.value = null
  selectedResultKtrs.value = []
  loadingKtrs.value = false
}

/**
 * Open user submissions modal and fetch all submissions for that user
 */
const openUserSubmissionsModal = async (userId, email) => {
  if (!userId) return
  
  selectedUserId.value = userId
  selectedUserEmail.value = email
  userSubmissions.value = []
  selectedResultKtrs.value = []
  loadingUserSubmissions.value = true
  loadingKtrs.value = true
  
  try {
    // Fetch all exam results for this user and KTRs in parallel
    const [allSubmissions, ktrs] = await Promise.all([
      getAllExamResultsByUserId(userId),
      getUserKtrs(userId)
    ])
    
    userSubmissions.value = allSubmissions
    selectedResultKtrs.value = ktrs
  } catch (error) {
    console.error('Error fetching user submissions:', error)
    userSubmissions.value = []
    selectedResultKtrs.value = []
  } finally {
    loadingUserSubmissions.value = false
    loadingKtrs.value = false
  }
}

/**
 * Close user submissions modal
 */
const closeUserSubmissionsModal = () => {
  selectedUserEmail.value = null
  selectedUserId.value = null
  userSubmissions.value = []
  selectedResultKtrs.value = []
  loadingUserSubmissions.value = false
  loadingKtrs.value = false
}

/**
 * Get submissions for a specific subject
 */
const getSubmissionsForSubject = (subjectId) => {
  return userSubmissions.value.filter(submission => submission.data.subjectId === subjectId)
}

/**
 * Toggle exam results table visibility for a subject
 */
const toggleSubjectTable = (subjectId) => {
  if (expandedSubjectTables.value.has(subjectId)) {
    expandedSubjectTables.value.delete(subjectId)
  } else {
    expandedSubjectTables.value.add(subjectId)
  }
}

/**
 * Toggle hide/show for a specific exam result
 */
const toggleHideResult = (resultId) => {
  if (hiddenResultIds.value.has(resultId)) {
    hiddenResultIds.value.delete(resultId)
  } else {
    hiddenResultIds.value.add(resultId)
  }
}

/**
 * Whether every result in the list is currently marked
 */
const areAllResultsMarked = (results) => {
  if (!results || results.length === 0) return false
  return results.every((result) => markedResultIds.value.has(result.id))
}

/**
 * Toggle marker for a single exam result
 */
const toggleResultMarker = async (result) => {
  if (!result?.id || markingResultIds.value.size > 0) return

  const resultId = result.id
  const yearMonth = formatScorecardYearMonth(currentMonth.value, currentYear.value)
  const shouldMark = !markedResultIds.value.has(resultId)

  markingResultIds.value.add(resultId)
  // trigger reactivity for Set mutations
  markingResultIds.value = new Set(markingResultIds.value)

  try {
    if (shouldMark) {
      await markScorecardResult(resultId, yearMonth)
      markedResultIds.value.add(resultId)
    } else {
      await unmarkScorecardResult(resultId)
      markedResultIds.value.delete(resultId)
    }
    markedResultIds.value = new Set(markedResultIds.value)
  } catch (error) {
    console.error('Error toggling scorecard marker:', error)
  } finally {
    markingResultIds.value.delete(resultId)
    markingResultIds.value = new Set(markingResultIds.value)
  }
}

/**
 * Toggle markers for all results in the current subject table
 */
const toggleAllMarkersForSubject = async (subjectGroup) => {
  if (!subjectGroup?.results?.length || markingResultIds.value.size > 0) return

  const resultIds = subjectGroup.results.map((result) => result.id)
  const shouldMark = !areAllResultsMarked(subjectGroup.results)
  const yearMonth = formatScorecardYearMonth(currentMonth.value, currentYear.value)

  markingResultIds.value = new Set(resultIds)

  try {
    await setScorecardMarkersForResults(resultIds, shouldMark, yearMonth)

    if (shouldMark) {
      resultIds.forEach((resultId) => markedResultIds.value.add(resultId))
    } else {
      resultIds.forEach((resultId) => markedResultIds.value.delete(resultId))
    }
    markedResultIds.value = new Set(markedResultIds.value)
  } catch (error) {
    console.error('Error toggling scorecard markers for subject:', error)
  } finally {
    markingResultIds.value = new Set()
  }
}

/**
 * Format CAAP date timestamp to readable string
 */
const formatCaapDate = (timestamp) => {
  if (!timestamp || !timestamp.seconds) return 'N/A'
  
  const date = new Date(timestamp.seconds * 1000)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * True if exam ms falls in any reconstructed confirmed CAAP Knowledge transaction window.
 * Windows use confirmation_date (else purchase_date) + duration_in_days + 5-day grace (ENG-15).
 */
const isExamInPriorTransactionPeriods = (examTimestamp, periods) => {
  if (!examTimestamp || !periods?.length) return false
  return periods.some(
    (period) => examTimestamp >= period.startMs && examTimestamp <= period.endMs
  )
}

/**
 * Filter exam results by CAAP activation dates for a specific subject.
 * Keeps submissions visible when examDate is in the current caap_data window
 * OR any confirmed caap-knowledge transaction window (renewal-safe; ENG-15).
 */
const filterResultsByCaapDates = async (subjectId) => {
  // Get the subject group
  const subjectGroup = examResultsBySubjectGrouped.value.find(sg => sg.subjectId === subjectId)
  if (!subjectGroup) return
  
  // Set loading state
  filteringSubjects.value.add(subjectId)
  
  try {
    // Get all unique userIds from this subject's results
    const uniqueUserIds = new Set()
    subjectGroup.results.forEach(result => {
      if (result.data.userId) {
        uniqueUserIds.add(result.data.userId)
      }
    })
    
    // Fetch current CAAP data + confirmed CAAP Knowledge transaction periods in parallel
    const userIdsArray = Array.from(uniqueUserIds)
    const [userCaapDataResults, userPeriodResults] = await Promise.all([
      Promise.all(
        userIdsArray.map((userId) =>
          getUserCaapData(userId).then((data) => ({ userId, data }))
        )
      ),
      Promise.all(
        userIdsArray.map((userId) =>
          getUserConfirmedCaapKnowledgePeriods(userId).then((periods) => ({ userId, periods }))
        )
      )
    ])
    
    const userCaapDataMap = new Map()
    userCaapDataResults.forEach(({ userId, data }) => {
      userCaapDataMap.set(userId, data)
    })

    const userPeriodsMap = new Map()
    userPeriodResults.forEach(({ userId, periods }) => {
      userPeriodsMap.set(userId, periods)
    })
    
    // Process each result
    subjectGroup.results.forEach(result => {
      const userId = result.data.userId
      const caapData = userCaapDataMap.get(userId)
      const priorPeriods = userPeriodsMap.get(userId) || []
      const examDate = result.data.examDate
      const examTimestamp = examDate?.seconds ? examDate.seconds * 1000 : null

      const isValidUnderPriorSubscription = isExamInPriorTransactionPeriods(
        examTimestamp,
        priorPeriods
      )
      
      if (!caapData || !caapData.activation_date || !caapData.expiration_date) {
        resultCaapDates.value.set(result.id, {
          activationDate: null,
          expirationDate: null,
          isWithinCurrentRange: false,
          isValidUnderPriorSubscription,
          isWithinRange: isValidUnderPriorSubscription
        })
        // Hide only when neither current caap_data nor prior transactions cover the exam
        if (!isValidUnderPriorSubscription) {
          hiddenResultIds.value.add(result.id)
        } else {
          hiddenResultIds.value.delete(result.id)
        }
        return
      }
      
      const activationTimestamp = caapData.activation_date?.seconds
        ? caapData.activation_date.seconds * 1000
        : null
      const expirationTimestamp = caapData.expiration_date?.seconds
        ? caapData.expiration_date.seconds * 1000
        : null
      
      let isWithinCurrentRange = false
      if (examTimestamp && activationTimestamp && expirationTimestamp) {
        isWithinCurrentRange =
          examTimestamp >= activationTimestamp && examTimestamp <= expirationTimestamp
      }

      const isWithinRange = isWithinCurrentRange || isValidUnderPriorSubscription
      
      resultCaapDates.value.set(result.id, {
        activationDate: caapData.activation_date,
        expirationDate: caapData.expiration_date,
        isWithinCurrentRange,
        isValidUnderPriorSubscription: isValidUnderPriorSubscription && !isWithinCurrentRange,
        isWithinRange
      })
      
      // Auto-hide only when outside current period and not covered by prior transactions
      if (!isWithinRange) {
        hiddenResultIds.value.add(result.id)
      } else {
        hiddenResultIds.value.delete(result.id)
      }
    })
  } catch (error) {
    console.error('Error filtering results by CAAP dates:', error)
  } finally {
    // Clear loading state
    filteringSubjects.value.delete(subjectId)
  }
}

/**
 * Computed property that calculates previous month's average scores by subjectId
 */
const previousMonthAverages = computed(() => {
  if (!previousMonthExamResults.value.length) {
    return new Map()
  }
  
  // Group by subjectId
  const grouped = new Map()
  
  previousMonthExamResults.value.forEach(result => {
    const subjectId = result.data.subjectId
    if (subjectId) {
      if (!grouped.has(subjectId)) {
        grouped.set(subjectId, [])
      }
      grouped.get(subjectId).push(result)
    }
  })
  
  // Calculate average score, averageItemsApp, and averageItemsExam for each subject
  const averages = new Map()
  
  grouped.forEach((results, subjectId) => {
    const validScores = results
      .map(r => r.data.score)
      .filter(score => score !== null && score !== undefined && !isNaN(score))
    
    const averageScore = validScores.length > 0
      ? validScores.reduce((sum, score) => sum + score, 0) / validScores.length
      : null
    
    // Calculate average of numItemsFromApp and average of numItemsInExam for previous month
    const validAppItems = results
      .map(r => r.data.numItemsFromApp)
      .filter(val => val !== null && val !== undefined && !isNaN(val))
    
    const validExamItems = results
      .map(r => r.data.numItemsInExam)
      .filter(val => val !== null && val !== undefined && !isNaN(val))
    
    const averageItemsApp = validAppItems.length > 0
      ? validAppItems.reduce((sum, val) => sum + val, 0) / validAppItems.length
      : null
    
    const averageItemsExam = validExamItems.length > 0
      ? validExamItems.reduce((sum, val) => sum + val, 0) / validExamItems.length
      : null
    
    // Calculate passing and failing rates for previous month
    const totalExamTakers = results.length
    const passedCount = results.filter(r => r.data.isPassed === true).length
    const failedCount = results.filter(r => r.data.isPassed === false).length
    
    const passingRate = totalExamTakers > 0
      ? (passedCount / totalExamTakers) * 100
      : null
    
    const failingRate = totalExamTakers > 0
      ? (failedCount / totalExamTakers) * 100
      : null
    
    averages.set(subjectId, {
      averageScore,
      averageItemsApp,
      averageItemsExam,
      passingRate,
      failingRate,
      totalExamTakers,
      passedCount,
      failedCount
    })
  })
  
  return averages
})

/**
 * Computed property that groups exam results by subjectId
 * Each group contains all exam results for that subject, sorted by most recent first
 */
const examResultsBySubjectGrouped = computed(() => {
  if (!examResults.value.length) {
    return []
  }
  
  // Group by subjectId
  const grouped = new Map()
  
  examResults.value.forEach(result => {
    const subjectId = result.data.subjectId
    if (subjectId) {
      if (!grouped.has(subjectId)) {
        grouped.set(subjectId, [])
      }
      grouped.get(subjectId).push(result)
    }
  })
  
  // Convert to array format and sort each group by examDate (most recent first)
  const subjectGroups = Array.from(grouped.entries()).map(([subjectId, results]) => {
    // Sort by examDate descending (most recent first)
    const sortedResults = results.sort((a, b) => {
      const dateA = a.data.examDate?.seconds || 0
      const dateB = b.data.examDate?.seconds || 0
      return dateB - dateA
    })
    
    // Calculate average score (excluding hidden results)
    const visibleResults = sortedResults.filter(r => !hiddenResultIds.value.has(r.id))
    const validScores = visibleResults
      .map(r => r.data.score)
      .filter(score => score !== null && score !== undefined && !isNaN(score))
    
    const averageScore = validScores.length > 0
      ? validScores.reduce((sum, score) => sum + score, 0) / validScores.length
      : 0
    
    // Calculate average of numItemsFromApp and average of numItemsInExam
    const validAppItems = visibleResults
      .map(r => r.data.numItemsFromApp)
      .filter(val => val !== null && val !== undefined && !isNaN(val))
    
    const validExamItems = visibleResults
      .map(r => r.data.numItemsInExam)
      .filter(val => val !== null && val !== undefined && !isNaN(val))
    
    const averageItemsApp = validAppItems.length > 0
      ? validAppItems.reduce((sum, val) => sum + val, 0) / validAppItems.length
      : null
    
    const averageItemsExam = validExamItems.length > 0
      ? validExamItems.reduce((sum, val) => sum + val, 0) / validExamItems.length
      : null
    
    // Calculate passing and failing rates
    const totalExamTakers = visibleResults.length
    const passedCount = visibleResults.filter(r => r.data.isPassed === true).length
    const failedCount = visibleResults.filter(r => r.data.isPassed === false).length
    
    const passingRate = totalExamTakers > 0
      ? (passedCount / totalExamTakers) * 100
      : null
    
    const failingRate = totalExamTakers > 0
      ? (failedCount / totalExamTakers) * 100
      : null
    
    // Get previous month's averages for this subject
    const previousMonthData = previousMonthAverages.value.get(subjectId)
    const previousMonthAverage = previousMonthData?.averageScore ?? null
    const previousMonthItemsApp = previousMonthData?.averageItemsApp ?? null
    const previousMonthItemsExam = previousMonthData?.averageItemsExam ?? null
    const previousMonthPassingRate = previousMonthData?.passingRate ?? null
    const previousMonthFailingRate = previousMonthData?.failingRate ?? null
    const previousMonthPassedCount = previousMonthData?.passedCount ?? null
    const previousMonthFailedCount = previousMonthData?.failedCount ?? null
    const previousMonthTotalExamTakers = previousMonthData?.totalExamTakers ?? null
    
    // Calculate variance for score (current - previous)
    const variance = previousMonthAverage !== null && previousMonthAverage !== undefined
      ? averageScore - previousMonthAverage
      : null
    
    // Calculate variance for App items (current - previous)
    const varianceItemsApp = previousMonthItemsApp !== null && previousMonthItemsApp !== undefined && averageItemsApp !== null
      ? averageItemsApp - previousMonthItemsApp
      : null
    
    // Calculate variance for Exam items (current - previous)
    const varianceItemsExam = previousMonthItemsExam !== null && previousMonthItemsExam !== undefined && averageItemsExam !== null
      ? averageItemsExam - previousMonthItemsExam
      : null
    
    // Calculate variance for passing rate (current - previous)
    const variancePassingRate = previousMonthPassingRate !== null && previousMonthPassingRate !== undefined && passingRate !== null
      ? passingRate - previousMonthPassingRate
      : null
    
    // Calculate variance for failing rate (current - previous)
    const varianceFailingRate = previousMonthFailingRate !== null && previousMonthFailingRate !== undefined && failingRate !== null
      ? failingRate - previousMonthFailingRate
      : null
    
    return {
      subjectId,
      results: sortedResults,
      averageScore,
      averageItemsApp,
      averageItemsExam,
      previousMonthAverage,
      previousMonthItemsApp,
      previousMonthItemsExam,
      previousMonthPassingRate,
      previousMonthFailingRate,
      previousMonthPassedCount,
      previousMonthFailedCount,
      previousMonthTotalExamTakers,
      variance,
      varianceItemsApp,
      varianceItemsExam,
      variancePassingRate,
      varianceFailingRate,
      passingRate,
      failingRate,
      totalExamTakers,
      passedCount,
      failedCount
    }
  })
  
  // Sort subjects alphabetically by name
  return subjectGroups.sort((a, b) => {
    const nameA = getSubjectName(a.subjectId) || ''
    const nameB = getSubjectName(b.subjectId) || ''
    return nameA.localeCompare(nameB)
  })
})

/**
 * Computed property that counts votes for each subtopic from exam results
 * Counts how many times each subtopic appears in subtopicVotes arrays
 */
const subtopicVoteCounts = computed(() => {
  if (!examResults.value.length) {
    return new Map()
  }
  
  const voteCounts = new Map()
  
  // Count votes for each subtopic across all exam results
  examResults.value.forEach(result => {
    const subtopicVotes = result.data.subtopicVotes || []
    
    subtopicVotes.forEach(subtopicId => {
      const currentCount = voteCounts.get(subtopicId) || 0
      voteCounts.set(subtopicId, currentCount + 1)
    })
  })
  
  return voteCounts
})

/**
 * Computed property that extracts all submitted questions from exam results
 * Flattens the addedQuestion arrays from all exam results
 */
const submittedQuestions = computed(() => {
  if (!examResults.value.length) {
    return []
  }
  
  const questions = []
  
  examResults.value.forEach(result => {
    const addedQuestions = result.data.addedQuestion || []
    
    addedQuestions.forEach(questionItem => {
      questions.push({
        userId: result.data.userId,
        email: result.data.email,
        subjectId: result.data.subjectId,
        question: questionItem.question || '',
        choices: questionItem.choices || []
      })
    })
  })
  
  return questions
})

/**
 * Computed property that groups subtopics under their respective subjects
 * Each subject contains a list of its subtopics with vote counts
 */
const subjectsWithSubtopics = computed(() => {
  if (!subjects.value.length || !subtopics.value.length) {
    return []
  }
  
  // Create a map of subtopics by ID for quick lookup
  const subtopicsMap = new Map()
  subtopics.value.forEach(subtopic => {
    subtopicsMap.set(subtopic.id, subtopic)
  })
  
  // Map each subject to include its subtopics with vote counts
  return subjects.value.map(subject => {
    const subjectSubtopics = []
    
    // Get subtopic IDs from subject.exam.subtopics array
    if (subject.data.exam && subject.data.exam.subtopics) {
      subject.data.exam.subtopics.forEach(subtopicId => {
        const subtopic = subtopicsMap.get(subtopicId)
        if (subtopic) {
          // Add vote count for this subtopic
          const voteCount = subtopicVoteCounts.value.get(subtopicId) || 0
          subjectSubtopics.push({
            ...subtopic,
            voteCount
          })
        }
      })
    }
    
    return {
      ...subject,
      subtopics: subjectSubtopics
    }
  })
})

const fetchData = async () => {
  loading.value = true
  try {
    const [subjectsData, subtopicsData] = await Promise.all([
      getCaapSubjects(),
      getCaapSubtopics()
    ])
    
    subjects.value = subjectsData
    subtopics.value = subtopicsData
  } catch (error) {
    console.error('Error fetching data:', error)
    subjects.value = []
    subtopics.value = []
  } finally {
    loading.value = false
  }
}

const fetchExamResults = async () => {
  loadingExamResults.value = true
  try {
    // Fetch current month and previous month data in parallel for efficiency
    const { month: prevMonth, year: prevYear } = getPreviousMonth(currentMonth.value, currentYear.value)
    
    const [currentResults, previousResults, markerIdsResult] = await Promise.all([
      getExamResultsByMonth(currentMonth.value, currentYear.value),
      getExamResultsByMonth(prevMonth, prevYear),
      getScorecardMarkersByMonth(currentMonth.value, currentYear.value).catch((error) => {
        console.error('Error fetching scorecard markers:', error)
        return []
      })
    ])
    
    examResults.value = currentResults
    previousMonthExamResults.value = previousResults
    markedResultIds.value = new Set(markerIdsResult)
    // Clear hidden results and CAAP dates when fetching new data (new month)
    hiddenResultIds.value.clear()
    resultCaapDates.value.clear()
  } catch (error) {
    console.error('Error fetching exam results:', error)
    examResults.value = []
    previousMonthExamResults.value = []
    markedResultIds.value = new Set()
  } finally {
    loadingExamResults.value = false
  }
}

// Watch for changes in examResults (actual data changes) and keep tables collapsed by default
// Only clear expanded tables when the actual exam results data changes, not when hiddenResultIds changes
watch(examResults, (newResults, oldResults) => {
  // Only clear if this is a new data fetch (different month), not just a recomputation
  // Check if the result IDs have changed (new data loaded)
  const newIds = new Set(newResults.map(r => r.id))
  const oldIds = oldResults ? new Set(oldResults.map(r => r.id)) : new Set()
  
  // Only clear if the IDs are different (new data loaded)
  if (newIds.size !== oldIds.size || ![...newIds].every(id => oldIds.has(id))) {
    expandedSubjectTables.value.clear()
  }
}, { immediate: false })

/**
 * Navigate to previous month
 */
const previousMonth = () => {
  if (loadingExamResults.value) return
  
  if (currentMonth.value === 0) {
    currentMonth.value = 11
    currentYear.value--
  } else {
    currentMonth.value--
  }
  fetchExamResults()
}

/**
 * Navigate to next month
 */
const nextMonth = () => {
  if (loadingExamResults.value) return
  
  if (currentMonth.value === 11) {
    currentMonth.value = 0
    currentYear.value++
  } else {
    currentMonth.value++
  }
  fetchExamResults()
}

onMounted(() => {
  fetchData()
  fetchExamResults()
})
</script>

<style scoped>
</style>

